"use client"

import { HomepageQueryResult, PageQueryResult } from "@/sanity.types";
import ColorScheme from "./colorScheme";
import CustomPortableText from "@/components/portable-text";
import { PortableTextBlock } from "next-sanity";
import { CSSProperties } from "react";
import Image from "next/image";
import Plus from "./Plus";
import { AnimatePresence, motion } from "motion/react";
import { useState, useEffect, SetStateAction } from "react";

interface CustomCSSProperties extends CSSProperties {
  '--color-accent'?: string;
}
type ImageWithAccordionsProps = Extract<NonNullable<NonNullable<HomepageQueryResult>["content"]>[number], { _type: "imageWithAccordions" }> | Extract<NonNullable<NonNullable<PageQueryResult>["content"]>[number], { _type: "imageWithAccordions" }>;
type BlockProps = NonNullable<ImageWithAccordionsProps["accordionBlocks"]>[0]

const Accordion = (
  {
    index,
    activeIndex,
    setActiveIndex,
    setLastIndex,
    block
  } : {
    index: number,
    activeIndex: number | null,
    setActiveIndex: React.Dispatch<SetStateAction<number | null>>,
    setLastIndex: React.Dispatch<SetStateAction<number | null>>
    block: BlockProps
  }) => {
  const { heading, content, image } = block;

  // activeIndex is the single source of truth for which panel is open.
  //
  // This used to also drive the native `open` attribute imperatively, toggling
  // it on click and stripping it when the close animation finished. That gave
  // us two sources of truth that drifted: the attribute trailed React state by
  // the 300ms exit transition, so a click landing in that window read a stale
  // "still open" and was swallowed. Reading `activeIndex` in the handler
  // instead is no better on its own, because two clicks in the same tick share
  // one closure and both see the same pre-update value.
  //
  // So `open` is now permanent and inert. AnimatePresence alone decides whether
  // the panel is mounted, and the functional setState below always sees the
  // live value regardless of how fast the clicks land.
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setActiveIndex(prev => (prev === index ? null : index));
    setLastIndex(index);
  }

  if (heading && content) {
    return (
      <details open>
        <summary onClick={handleClick} className={`flex justify-between gap-2.5 p-2.5 cursor-pointer list-none [&::-webkit-details-marker]:hidden border-b-4 transition-[border-color] hover:border-sky ${index == activeIndex ? 'border-sky' : 'border-transparent'}`}>
          <span className="body--large">{block.heading}</span>
          {/* Icon is deliberately basil rather than the section accent: the
              accent (sun) also colors the circled word in the heading, which
              should stay yellow while the +/× reads green. */}
          <span className={`${index == activeIndex? 'rotate-135' : ''} transition-transform shrink-0 text-basil`}><Plus /></span>
        </summary>
        <AnimatePresence>
          {index == activeIndex &&
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              {/* max-md:[&_p]:text-base = one type-scale step down (body ->
                  body--small size) on mobile only. */}
              <CustomPortableText value={block.content as PortableTextBlock[]} className="px-2.5 pt-2.5 pb-5 text-greyscale-clay max-md:[&_p]:text-base"/>
              {/* Mobile: each panel carries its own image in a fixed
                  340x300 container (capped to the panel width on narrow
                  phones); desktop uses the shared stack instead. */}
              {image && image.assetPath &&
                <div className="md:hidden relative w-85 max-w-full h-75 mx-auto mb-5">
                  <Image
                    src={image.assetPath}
                    alt={image.caption || ""}
                    fill={true}
                    loading="lazy"
                    sizes="100vw"
                    className="object-contain"
                  />
                </div>
              }
            </motion.div>
          }
        </AnimatePresence>
      </details>
    )
  }
}

const AccordionImage = (
  {
    index,
    activeIndex,
    lastIndex,
    block
  } : {
    index: number,
    activeIndex: number | null,
    lastIndex: number | null,
    block: BlockProps
  }) => {
  const { image } = block;

  if (image && image.assetPath) {
    return (
      <Image
        src={image.assetPath}
        alt={image.caption || ""}
        fill={true}
        loading="lazy"
        sizes="(min-width: 1024px) 50vw, 200vw"
        className={`${index == activeIndex || (index == lastIndex && activeIndex == null) || (index == 0 && lastIndex == null && activeIndex == null)? '' : 'opacity-0'} transition-opacity object-contain md:object-top`}
      />
    )
  }
}

export default function ImageWithAccordions(props: ImageWithAccordionsProps) {
  const {content, accentColor, accordionBlocks, colorScheme} = props;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [lastIndex, setLastIndex] = useState<number | null>(null);

  // Mobile starts with the first accordion open (the image lives inside the
  // panel there, so a closed list would show no image at all). Desktop keeps
  // everything closed and shows the first image in the shared stack instead.
  useEffect(() => {
    if (window.matchMedia('(max-width: 767px)').matches) {
      setActiveIndex(0);
      setLastIndex(0);
    }
  }, []);

  const style: CustomCSSProperties = {
    '--color-accent': accentColor ? `var(--color-${accentColor})` : 'var(--color-sky)',
  }

  return (
    <section style={style}>
      <ColorScheme colorScheme={colorScheme && colorScheme != 'default'? colorScheme : null}>
        {/* max-md:pb-30 (120px) balances the mobile section: 20px padding +
            100px heading top margin above = 120px of space above the title. */}
        <div className="bg-(--color-background) text-(--color-foreground) p-5 max-md:pb-30 md:py-35 md:px-7.5 marketing-container">
          {/* The intro rides the same 12-col grid as the image + accordions
              below (cols 2-11), so its edges line up with the section. */}
          {content &&
            <div className="max-md:mt-25 mb-15 md:grid md:grid-cols-12 md:gap-x-10">
              <CustomPortableText value={content as PortableTextBlock[]} className="text-center space-y-7.5 md:col-start-2 md:col-span-10" />
            </div>
          }
          {accordionBlocks &&
            // gap-x-10 (40px) separates the image column from the accordion
            // column; the intro grid above carries the same gap so the outer
            // edges of both grids stay aligned.
            <div className="md:grid md:grid-cols-12 md:gap-x-10">
              <div className="max-md:hidden relative aspect-square md:col-start-2 md:col-span-5 md:mt-2.5">
                {accordionBlocks.map((block, i) =>
                  <AccordionImage key={i} index={i} activeIndex={activeIndex} lastIndex={lastIndex} block={block} />
                )}
              </div>
              <div className="md:col-start-7 md:col-span-5">
                {accordionBlocks.map((block, i) =>
                  <Accordion key={i} index={i} activeIndex={activeIndex} setActiveIndex={setActiveIndex} setLastIndex={setLastIndex} block={block} />
                )}
              </div>
            </div>
          }
        </div>
      </ColorScheme>
    </section>
  )
}